import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Select, Spin } from '@arco-design/web-react';
import {
  IconCheckCircle,
  IconEdit,
  IconExclamationCircle,
  IconFile,
  IconMessage,
  IconNotification,
  IconRefresh,
  IconRight,
  IconUser
} from '@arco-design/web-react/icon';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import MetricCard from '../components/dashboard/MetricCard';
import Panel from '../components/dashboard/Panel';
import {
  fetchWorkbenchOverview,
  formatDateTime,
  formatNumber,
  formatPercent,
  formatWaiting,
  WorkbenchOverview
} from '../service/workbench';
import '../components/dashboard/index.scss';
import './index.scss';

interface WelcomeProps {
  username?: string;
}

/** 分组展示顺序：千万 → 百万 → 十万 → 六一 */
const GROUP_PRIORITY = ['千万组', '百万组', '十万组', '六一组'];

const groupRank = (name?: string) => {
  const index = GROUP_PRIORITY.indexOf(name || '');
  return index === -1 ? GROUP_PRIORITY.length : index;
};

/** 09:00 前报单算前一报单日，所以下一个 09:00 就是当前报单日的截止时间 */
const resolveDeadline = () => {
  const now = dayjs();
  const todayNine = now.startOf('day').add(9, 'hour');
  return now.isBefore(todayNine) ? todayNine : todayNine.add(1, 'day');
};

const formatCountdown = (minutes: number) => {
  if (minutes <= 0) return '已截止';
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const rest = minutes % 60;
  if (days > 0) return `${days} 天 ${hours} 小时`;
  if (hours > 0) return `${hours} 小时 ${rest} 分`;
  return `${rest} 分钟`;
};

export default function Welcome({ username = '管理员' }: WelcomeProps) {
  const navigate = useNavigate();
  const [session, setSession] = useState<string>();
  const [data, setData] = useState<WorkbenchOverview>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [queueGroup, setQueueGroup] = useState('');
  const [queueSort, setQueueSort] = useState<'waiting' | 'earnings'>('waiting');
  const [queueHeight, setQueueHeight] = useState<number>();
  const sideColumnRef = useRef<HTMLElement>(null);
  const queueWrapRef = useRef<HTMLDivElement>(null);

  /** 桌面端：队列高度 = 右列最后一块（今日报单进度）底部到队列顶部的距离，数据多少都不改变高度 */
  useLayoutEffect(() => {
    const compute = () => {
      const side = sideColumnRef.current;
      const wrap = queueWrapRef.current;
      if (!side || !wrap || window.innerWidth <= 1100) {
        setQueueHeight(undefined);
        return;
      }
      const lastPanel = side.lastElementChild as HTMLElement | null;
      if (!lastPanel) return;
      const bottom = lastPanel.getBoundingClientRect().bottom;
      const top = wrap.getBoundingClientRect().top;
      setQueueHeight(Math.max(280, Math.round(bottom - top)));
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [data]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    fetchWorkbenchOverview({ days: 1, session })
      .then(result => {
        if (alive) setData(result);
      })
      .catch((requestError: Error) => {
        if (alive) setError(requestError.message || '工作台数据加载失败，请稍后重试');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [session, revision]);

  const refresh = () => setRevision(value => value + 1);
  const todoTotal = data
    ? data.todo.riskCommentPending +
      data.todo.rankCommentPending +
      data.todo.abnormalOrders +
      data.todo.penaltyPending +
      data.todo.reportCountToday
    : 0;

  const reportDate = data?.queryDate || dayjs().format('YYYY-MM-DD');
  const deadline = useMemo(() => resolveDeadline(), [revision, data?.updatedAt]);
  const countdown = formatCountdown(deadline.diff(dayjs(), 'minute'));

  const queueGroups = useMemo(
    () => (data?.groups || []).map(group => group.name).filter(Boolean).sort((a, b) => groupRank(a) - groupRank(b)),
    [data]
  );

  const queueRows = useMemo(() => {
    const rows = (data?.queue || []).filter(item => !queueGroup || item.groupName === queueGroup);
    return rows.slice().sort((a, b) =>
      queueSort === 'earnings'
        ? (Number(b.todayEarningRate) || 0) - (Number(a.todayEarningRate) || 0)
        : b.waitingMinutes - a.waitingMinutes
    );
  }, [data, queueGroup, queueSort]);

  /** 数据回来后默认落在第一个有审核记录的分组，没有则落在第一个分组 */
  useEffect(() => {
    if (!data || !queueGroups.length) return;
    if (queueGroup && queueGroups.includes(queueGroup)) return;
    const firstWithRows = queueGroups.find(name => data.queue.some(item => item.groupName === name));
    setQueueGroup(firstWithRows || queueGroups[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, queueGroups]);

  /** 报单数据异常跳转：自动定位到当天有报单的最高资产分组（千万 → 百万 → 十万 → 六一） */
  const abnormalGroupType = useMemo(() => {
    for (const type of [103, 100, 101, 102]) {
      const group = (data?.groups || []).find(item => item.groupType === type);
      if (group && group.reported > 0) return type;
    }
    return undefined;
  }, [data]);

  const openOrderList = (extra: Record<string, string> = {}) => {
    const params = new URLSearchParams({ holdDate: reportDate });
    if (data?.session) params.set('gameSession', data.session);
    Object.entries(extra).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    navigate(`/?${params.toString()}`);
  };

  const openQueueItem = (item: WorkbenchOverview['queue'][number]) => {
    openOrderList({
      openId: item.openId || '',
      groupType: item.groupType ? String(item.groupType) : ''
    });
  };

  return (
    <div className="workbench-page">
      <div className="page-heading">
        <div>
          <div className="page-breadcrumb">工作台 / 运营概览</div>
          <h1>你好，{username}</h1>
          <p>今天有 {formatNumber(todoTotal)} 项待办，优先处理报单与异常数据。</p>
        </div>
        <Select
          aria-label="选择赛季"
          value={session || data?.session}
          loading={!data && loading}
          placeholder="选择赛季"
          onChange={setSession}
          style={{ width: 164 }}
        >
          {data?.seasons.map(item => (
            <Select.Option key={item.gameSession} value={item.gameSession}>
              {item.gameSessionName || item.gameSession}
            </Select.Option>
          ))}
        </Select>
      </div>

      {error && <Alert className="page-alert" type="error" content={error} action={<Button onClick={refresh}>重试</Button>} />}
      <Spin loading={loading} style={{ width: '100%' }}>
        {data && (
          <>
            <section className="priority-banner">
              <div>
                <span>今日优先事项 · 截至 {formatDateTime(data.updatedAt).slice(11)}</span>
                <h2>距离报单截止还有 {countdown}</h2>
                <p>
                  {formatNumber(data.today.unreportedUsers)} 人未报单 · {formatNumber(data.today.overdueOrders)} 条审核超时 ·{' '}
                  {formatNumber(data.today.abnormalOrders)} 条数据异常
                </p>
              </div>
              <Button type="primary" icon={<IconRefresh />} onClick={refresh}>
                刷新
              </Button>
            </section>

            <div className="metric-grid">
              <MetricCard
                title="今日报单 / 人"
                value={data.today.reportedUsers}
                hint={`较昨日 ${data.today.reportedUserChange >= 0 ? '+' : ''}${formatNumber(data.today.reportedUserChange)}`}
              />
              <MetricCard title="报单完成率" value={data.today.completionRate} percent hint={`应报单 ${formatNumber(data.today.expectedUsers)} 人`} />
              <MetricCard title="待确认报单 / 条" value={data.today.pendingOrders} emphasis hint={`其中 ${formatNumber(data.today.overdueOrders)} 条等待超过 2 小时`} />
              <MetricCard title="风险评论 / 条" value={data.todo.riskCommentPending} hint="待审核 · 当天" />
              <MetricCard title="高手榜评论 / 条" value={data.todo.rankCommentPending} hint="待审核 · 当天" />
              <MetricCard title="今日活跃用户 / 人" value={data.today.activeUsers} change={data.today.activeUserChangeRate} changeLabel="较昨日" />
            </div>

            <div className="workbench-grid">
              <div className="workbench-main-column">
                <Panel title={`待办中心 ${todoTotal} 项`} extra="按处理优先级排序">
                  <TodoRow
                    count={data.todo.riskCommentPending}
                    title="风险评论待处理"
                    description="当天评论中的风险内容，等待审核"
                    action="去审核"
                    onClick={() => navigate(`/comment?date=${reportDate}&riskLevel=all`)}
                  />
                  <TodoRow
                    count={data.todo.rankCommentPending}
                    title="高手榜评论待处理"
                    description="当天的高手榜风险评论，等待审核"
                    action="待审核"
                    onClick={() => navigate(`/rankTopComment?date=${reportDate}&gameSession=${data.session}&riskLevel=all`)}
                  />
                  <TodoRow
                    count={data.todo.abnormalOrders}
                    title="报单数据异常"
                    description="今日收益率 ≥ 30%，需要人工复核"
                    action="查看异常"
                    tag="优先处理"
                    onClick={() => openOrderList({ totalEarningSort: 'desc', groupType: abnormalGroupType ? String(abnormalGroupType) : '' })}
                  />
                  <TodoRow
                    count={data.todo.penaltyPending}
                    title="红黄牌待审核"
                    description="处罚取消申请等待复核"
                    action="去处理"
                    onClick={() => navigate(`/order/penalty-log?penaltyDate=${reportDate}&penaltySource=all`)}
                  />
                  <TodoRow
                    count={data.todo.reportCountToday}
                    title="今日新增内容举报"
                    description="举报处理状态暂未接入，先查看举报记录"
                    action="去处理"
                    onClick={() => navigate('/notebook-report')}
                  />
                </Panel>

                <div
                  className="queue-wrap"
                  ref={queueWrapRef}
                  style={queueHeight ? { height: queueHeight } : undefined}
                >
                <Panel
                  title="重点审核队列"
                  extra={
                    <div className="queue-sort">
                      <button
                        className={queueSort === 'waiting' ? 'is-active' : ''}
                        onClick={() => setQueueSort('waiting')}
                      >
                        等待时长
                      </button>
                      <button
                        className={queueSort === 'earnings' ? 'is-active' : ''}
                        onClick={() => setQueueSort('earnings')}
                      >
                        收益率
                      </button>
                    </div>
                  }
                  className="queue-panel"
                >
                  <div className="queue-tabs">
                    {queueGroups.map(name => {
                      const count = data.queue.filter(item => item.groupName === name).length;
                      return (
                        <button
                          key={name}
                          className={name === queueGroup ? 'is-active' : ''}
                          onClick={() => setQueueGroup(name)}
                        >
                          {name}
                          <em>{count}</em>
                        </button>
                      );
                    })}
                  </div>
                  {queueRows.length ? (
                    <div className="data-table-wrapper">
                      <table className="data-table">
                        <thead><tr><th>参赛用户</th><th>问题摘要</th><th>等待时长</th><th>优先级</th><th>操作</th></tr></thead>
                        <tbody>
                          {queueRows.map(item => (
                            <tr key={item.id}>
                              <td>{item.gameName || '—'}</td><td>{item.issueSummary}</td>
                              <td>{formatWaiting(item.waitingMinutes)}</td><td><span className="soft-tag">{item.priority}优先级</span></td>
                              <td><button className="text-link" onClick={() => openQueueItem(item)}>进入审核</button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <div className="dashboard-empty">当前没有待审核报单</div>}
                </Panel>
                </div>
              </div>

              <aside className="workbench-side-column" ref={sideColumnRef}>
                <Panel title="当前赛事" extra={<span className="soft-tag">{data.currentGame.gameStatusText || '进行中'}</span>}>
                  <h3 className="current-game__name">{data.currentGame.gameSessionName || data.currentGame.gameSession}</h3>
                  <p className="muted-text">参赛人数 {formatNumber(data.currentGame.participantCount)} 人 · {formatNumber(data.currentGame.groupCount)} 个分组</p>
                  <div className="current-game__dates">
                    <span>今日报单截止</span>
                    <strong>{deadline.format('MM-DD 09:00')} · 还有 {countdown}</strong>
                  </div>
                  <button className="text-link" onClick={() => navigate('/game/index')}>查看赛事配置 <IconRight /></button>
                </Panel>

                <Panel title="快捷入口">
                  <div className="quick-grid">
                    <QuickLink icon={<IconFile />} label="报单列表" onClick={() => openOrderList()} />
                    <QuickLink icon={<IconCheckCircle />} label="比赛报名" onClick={() => navigate('/pay')} />
                    <QuickLink icon={<IconNotification />} label="发布公告" onClick={() => navigate('/announcement')} />
                    <QuickLink icon={<IconUser />} label="用户查询" onClick={() => navigate('/user')} />
                    <QuickLink icon={<IconEdit />} label="笔记管理" onClick={() => navigate('/note')} />
                  </div>
                </Panel>

                <Panel title="今日报单进度" extra={`${data.queryDate} · 截至 ${formatDateTime(data.updatedAt).slice(11)}`}>
                  <div className="report-progress__summary">
                    <span>已报单 <strong>{formatNumber(data.today.reportedUsers)}</strong> / {formatNumber(data.today.expectedUsers)} 人</span>
                    <b>{formatPercent(data.today.completionRate)}</b>
                  </div>
                  <div className="report-progress__track" aria-label={`报单完成率 ${formatPercent(data.today.completionRate)}`}>
                    <span style={{ width: `${Math.min(100, Math.max(0, data.today.completionRate || 0))}%` }} />
                  </div>
                  <p className="muted-text">仍有 {formatNumber(data.today.unreportedUsers)} 人未报单</p>
                  <div className="data-table-wrapper">
                    <table className="data-table">
                      <thead><tr><th>参赛分组</th><th>已报 / 应报</th><th>完成率</th><th>待确认</th></tr></thead>
                      <tbody>
                        {data.groups.map(group => (
                          <tr key={group.groupType}>
                            <td>{group.name}</td><td>{formatNumber(group.reported)} / {formatNumber(group.expected)}</td>
                            <td>{formatPercent(group.completionRate)}</td><td>{formatNumber(group.pending)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Panel>
              </aside>
            </div>

            {data.dataWarnings.length > 0 && <p className="data-warning"><IconExclamationCircle /> {data.dataWarnings.join('；')}</p>}
          </>
        )}
      </Spin>
      <footer className="page-footer"><IconMessage /> 福利赛事 · 运营管理工作台</footer>
    </div>
  );
}

function QuickLink({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return <button className="quick-link" onClick={onClick}>{icon}<span>{label}</span></button>;
}

function TodoRow({ count, title, description, action, tag, onClick }: { count: number; title: string; description: string; action: string; tag?: string; onClick: () => void }) {
  return (
    <div className="todo-row">
      <span className="todo-row__count">{String(count || 0).padStart(2, '0')}</span>
      <div className="todo-row__content"><strong>{title}{tag && <em>{tag}</em>}</strong><span>{description}</span></div>
      <button className="text-link" onClick={onClick}>{action} <IconRight /></button>
    </div>
  );
}


